import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useState } from 'react';
import DateUtils from 'utils/DateUtils';
import AuditTypeBadge from 'core/badges/AuditTypeBadge';

const sx = {
  jsonValue: {
    mt: 0,
    p: 1.25,
    backgroundColor: '#f7f9fc',
    borderRadius: '0 0 6px 6px',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    border: '1px solid',
    borderColor: 'divider',
    borderTop: 'none',
  },
  logToggleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    px: 1.5,
    py: 0.75,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: 'action.hover' },
  },
  logindexdata: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    backgroundColor: 'primary.main',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
  },
  paginationButton: {
    minWidth: 80,
    height: 30,
    backgroundColor: '#0d4f8b',
    color: '#fff',
  },
};

const formatJson = (value) => {
  if (value == null || value === '') return '-';
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) && parsed.length === 0
      ? 'No any value changes'
      : JSON.stringify(parsed, null, 2);
  } catch {
    return value;
  }
};

function JsonToggleField({ label, value }) {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => {
    setOpen(!open);
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Box component="button" onClick={() => toggleOpen()} sx={[sx.logToggleButton]}>
        <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
          {label}
        </Typography>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box component="pre" sx={sx.jsonValue}>
          {formatJson(value)}
        </Box>
      </Collapse>
    </Box>
  );
}

function AuditLogEntry({ log, index, isLast }) {
  return (
    <Box sx={{ pb: isLast ? 0 : 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={sx.logindexdata}>{index + 1}</Box>
        <Box sx={{ pl: 0.5, display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
          <AuditTypeBadge auditType={log?.auditType} />
          <Typography variant="body2" color="text.secondary">
            {log?.changedBy || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {log?.changedDate ? DateUtils.isoToString(log.changedDate) : '-'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ pl: 4.5 }}>
        <JsonToggleField label="Added value" value={log?.addedValue} />
        <JsonToggleField label="Removed value" value={log?.removedValue} />
      </Box>

      {!isLast && <Divider sx={{ mt: 3 }} />}
    </Box>
  );
}

function AuditLogDialog({ open, onClose, isLoading, logs = [], pagination, onPageChange }) {
  const { currentPage, totalPage } = pagination;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh', maxWidth: '40vw' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6" fontWeight={600}>
          Audit Logs
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : logs.length === 0 ? (
          <Typography color="text.secondary">No audit logs found for this role.</Typography>
        ) : (
          logs.map((log, i) => (
            <AuditLogEntry key={i} log={log} index={i} isLast={i === logs.length - 1} />
          ))
        )}

        {!isLoading && logs.length > 0 && totalPage > 1 && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={3}>
            <Button
              variant="contained"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              sx={sx.paginationButton}
            >
              Previous
            </Button>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {totalPage}
            </Typography>
            <Button
              variant="contained"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPage}
              sx={sx.paginationButton}
            >
              Next
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AuditLogDialog;
